import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaPaperPlane, FaCheckCircle, FaExclamationCircle, FaWhatsapp } from 'react-icons/fa';
import emailjs from 'emailjs-com';
import './Contact.css';

const SERVICE_ID = 'SEU_SERVICE_ID'; // Substitua pelo seu Service ID do EmailJS
const TEMPLATE_ID = 'SEU_TEMPLATE_ID'; // Substitua pelo seu Template ID do EmailJS
const PUBLIC_KEY = 'SEU_PUBLIC_KEY'; // Substitua pelo seu Public Key do EmailJS

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    honeypot: '' // Campo honeypot para detectar bots
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const honeypotRef = useRef(null);
  const formRef = useRef();
  const isMobile = window.innerWidth <= 768;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Verificar se é um bot (honeypot)
  const isBot = () => {
    return formData.honeypot !== '';
  };

  // Verificar reCAPTCHA
  const isRecaptchaValid = () => {
    return recaptchaToken !== '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isBot()) {
      console.log('Bot detectado pelo honeypot');
      return;
    }
    if (!isRecaptchaValid()) {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus(null), 5000);
      return;
    }
    setIsSubmitting(true);
    setSubmitStatus(null);
    try {
      await emailjs.sendForm(
        SERVICE_ID,
        TEMPLATE_ID,
        formRef.current,
        PUBLIC_KEY
      );
      setSubmitStatus('success');
      setFormData({ name: '', email: '', message: '', honeypot: '' });
      setRecaptchaToken('');
      setTimeout(() => setSubmitStatus(null), 5000);
    } catch (error) {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus(null), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: <FaEnvelope />,
      title: "E-mail",
      value: "contato@microcodie.com.br",
      link: "mailto:contato@microcodie.com.br"
    },
    {
      icon: <FaPhone />,
      title: "Telefone",
      value: "(31) 99517-0464",
      link: "tel:+5531995170464"
    },
    {
      icon: <FaMapMarkerAlt />,
      title: "Endereço",
      value: "Viçosa, MG - Brasil",
      link: "https://g.co/kgs/48QmejT"
    }
  ];

  return (
    <section id="contato" className="section contact-section">
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="section-title">Entre em Contato</h2>
          <p className="section-subtitle">Vamos transformar sua ideia em realidade</p>
        </motion.div>

        <div className="contact-content">
          <motion.div
            className="contact-form-container"
            initial={isMobile ? { opacity: 0, y: 50 } : { opacity: 0, x: -50 }}
            whileInView={isMobile ? { opacity: 1, y: 0 } : { opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="contact-form-wrapper">
              <h3>Envie sua mensagem</h3>
              <p>Preencha o formulário abaixo e entraremos em contato em breve</p>
              
              <form ref={formRef} onSubmit={handleSubmit} className="contact-form">
                {/* Campo honeypot (escondido) */}
                <div className="honeypot-field" style={{ display: 'none' }}>
                  <input
                    ref={honeypotRef}
                    type="text"
                    name="honeypot"
                    value={formData.honeypot}
                    onChange={handleInputChange}
                    tabIndex="-1"
                    autoComplete="off"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="name">Nome completo</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Digite seu nome completo"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">E-mail</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="Digite seu e-mail"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="message">Mensagem</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows="5"
                    placeholder="Conte-nos sobre seu projeto ou ideia"
                  />
                </div>

                {/* reCAPTCHA */}
                <div className="form-group">
                  <div className="recaptcha-container">
                    <div 
                      className="recaptcha-placeholder"
                      onClick={() => {
                        // Simular verificação do reCAPTCHA
                        if (!recaptchaToken) {
                          setRecaptchaToken('verified');
                        }
                      }}
                    >
                      {recaptchaToken ? (
                        <div className="recaptcha-checked">
                          <FaCheckCircle />
                          <span>Verificado</span>
                        </div>
                      ) : (
                        <div className="recaptcha-unchecked">
                          <div className="recaptcha-checkbox"></div>
                          <span>Não sou um robô</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <button 
                  type="submit" 
                  className="btn btn-primary submit-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Enviando...' : (
                    <>
                      Enviar mensagem
                      <FaPaperPlane />
                    </>
                  )}
                </button>
              </form>

              {/* Feedback de envio */}
              <AnimatePresence>
                {submitStatus && (
                  <motion.div
                    className={`submit-feedback ${submitStatus}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    {submitStatus === 'success' ? (
                      <>
                        <FaCheckCircle />
                        <span>Mensagem enviada com sucesso! Entraremos em contato em breve.</span>
                      </>
                    ) : (
                      <>
                        <FaExclamationCircle />
                        <span>Erro ao enviar mensagem. Verifique o reCAPTCHA e tente novamente.</span>
                      </>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>

          <motion.div
            className="contact-info-container"
            initial={isMobile ? { opacity: 0, y: 50 } : { opacity: 0, x: 50 }}
            whileInView={isMobile ? { opacity: 1, y: 0 } : { opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <div className="contact-info">
              <h3>Informações de contato</h3>
              <p>Entre em contato conosco através dos canais abaixo</p>
              
              <div className="contact-items">
                {contactInfo.map((item, index) => (
                  <motion.a
                    key={index}
                    href={item.link}
                    className="contact-item"
                    whileHover={{ x: 10 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="contact-icon">
                      {item.icon}
                    </div>
                    <div className="contact-details">
                      <h4>{item.title}</h4>
                      <span>{item.value}</span>
                    </div>
                  </motion.a>
                ))}
              </div>
            </div>

            <div className="contact-map">
              <h4>Nossa localização</h4>
              <div className="map-container">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2360.852359260749!2d-42.864840882414924!3d-20.71442553983212!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xa3675f9344b095%3A0xfa1bda55ae43a7e9!2stecnoPARQ%20-%20Parque%20Tecnol%C3%B3gico%20de%20Vi%C3%A7osa!5e1!3m2!1spt-BR!2sbr!4v1753123831691!5m2!1spt-BR!2sbr" 
                  width="100%"
                  height="300"
                  style={{ border: 0, borderRadius: 'var(--border-radius)' }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="MicroCodie - Localização"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Botão flutuante do WhatsApp */}
      <motion.a
        href="https://wa.me/5531995170464?text=Olá! Gostaria de saber mais sobre os serviços da MicroCodie."
        target="_blank"
        rel="noopener noreferrer"
        className="whatsapp-float"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1, duration: 0.5 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        <FaWhatsapp />
        <span className="whatsapp-tooltip">Fale conosco no WhatsApp</span>
      </motion.a>
    </section>
  );
};

export default Contact; 